import '../styles/TextEditor.scss';

import {
  Code,
  LinkSimpleHorizontal,
  ListDashes,
  ListNumbers,
  Quotes,
  TextAlignCenter,
  TextAlignJustify,
  TextAlignLeft,
  TextAlignRight,
  TextB,
  TextHOne,
  TextHThree,
  TextHTwo,
  TextItalic,
  TextUnderline,
} from '@phosphor-icons/react';
import { forwardRef, MouseEvent, PropsWithChildren } from 'react';
import { Editor, Element as SlateElement, Transforms } from 'slate';
import { useSlate } from 'slate-react';

import { cn } from '../../../helpers/classBuilders';
import { useClassHelper } from '../../../hooks/useClassHelper';

const componentName = 'c-text-editor';

const LIST_TYPES = ['numbered-list', 'bulleted-list'];
const TEXT_ALIGN_TYPES = ['left', 'center', 'right', 'justify'];

interface ButtonProps extends PropsWithChildren {
  title: string;
  className?: string;
  active: boolean;
  onMouseDown: (event: MouseEvent<HTMLButtonElement>) => void;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function EditorButton({ className, active, ...props }, ref) {
    return (
      <button
        type="button"
        ref={ref}
        {...props}
        className={`${className} ${
          props.title
        } ${componentName}__button focus-visible:focus-ring-form-inset ${
          active ? 'active' : ''
        }`}
      />
    );
  },
);

interface ToolbarProps {
  className?: string;
  active?: boolean;
  children?: any;
}

export const Toolbar = forwardRef<HTMLButtonElement, ToolbarProps>(
  function Toolbar(props, ref) {
    const c = useClassHelper(componentName);

    return (
      <span
        {...props}
        ref={ref}
        className={`${c('__toolbar', props.className)} ${cn(
          'border border-gray-300',
        )}`}
      />
    );
  },
);

const getNewProperties = (format, isActive, isList) => {
  if (TEXT_ALIGN_TYPES.includes(format)) {
    return {
      align: isActive ? undefined : format,
    };
  } else {
    return {
      type: isActive ? 'paragraph' : isList ? 'list-item' : format,
    };
  }
};

const toggleBlock = (editor, format) => {
  const isActive = isBlockActive(
    editor,
    format,
    TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type',
  );
  const isList = LIST_TYPES.includes(format);

  Transforms.unwrapNodes(editor, {
    match: n =>
      !Editor.isEditor(n) &&
      SlateElement.isElement(n) &&
      LIST_TYPES.includes((n as any).type) &&
      !TEXT_ALIGN_TYPES.includes(format),
    split: true,
  });

  const newProperties: any = getNewProperties(format, isActive, isList);

  Transforms.setNodes(editor, newProperties);

  if (!isActive && isList) {
    const block = { type: format, children: [] };
    Transforms.wrapNodes(editor, block);
  }
};

export const toggleMark = (editor, format) => {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
};

const isBlockActive = (editor, format, blockType = 'type') => {
  const { selection } = editor;
  if (!selection) return false;

  const [match] = Array.from(
    Editor.nodes(editor, {
      at: Editor.unhangRange(editor, selection),
      match: n =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        n[blockType] === format,
    }),
  );

  return !!match;
};

const isMarkActive = (editor, format) => {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
};

// switch statements are allowed to avoid complexity rule

export const Element = ({ attributes, children, element }) => {
  const style = { textAlign: element.align || 'left' };
  const Tag = (() => {
    switch (element.type) {
      case 'heading-one':
        return 'h1';
      case 'heading-two':
        return 'h2';
      case 'heading-three':
        return 'h3';
      case 'block-quote':
        return 'blockquote';
      case 'bulleted-list':
        return 'ul';
      case 'numbered-list':
        return 'ol';
      case 'list-item':
        return 'li';
      default:
        return 'p';
    }
  })();

  return (
    <Tag {...attributes} style={style} className={`${componentName}__${Tag}`}>
      {children}
    </Tag>
  );
};

export const Leaf = ({ attributes, children, leaf }) => {
  if (leaf.bold) children = <strong>{children}</strong>;
  if (leaf.code) children = <code>{children}</code>;
  if (leaf.italic) children = <em>{children}</em>;
  if (leaf.underline) children = <u>{children}</u>;
  if (leaf.link) children = <a href="/">{children}</a>;

  return <span {...attributes}>{children}</span>;
};

export const BlockButton = ({ format }) => {
  const editor = useSlate();

  return (
    <Button
      title={format}
      active={isBlockActive(
        editor,
        format,
        TEXT_ALIGN_TYPES.includes(format) ? 'align' : 'type',
      )}
      onMouseDown={event => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
    >
      {iconsList[format.replace('-', '_')]}
    </Button>
  );
};

export const MarkButton = ({ format }) => {
  const editor = useSlate();

  return (
    <Button
      title={format}
      active={isMarkActive(editor, format)}
      onMouseDown={event => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
    >
      {iconsList[format.replace('-', '_')]}
    </Button>
  );
};

const iconsList = {
  bold: <TextB size={32} weight="bold" />,
  italic: <TextItalic size={32} />,
  underline: <TextUnderline size={32} />,
  code: <Code size={32} />,
  heading_one: <TextHOne size={32} />,
  heading_two: <TextHTwo size={32} />,
  heading_three: <TextHThree size={32} />,
  block_quote: <Quotes size={32} />,
  link: <LinkSimpleHorizontal size={32} />,
  numbered_list: <ListNumbers size={32} />,
  bulleted_list: <ListDashes size={32} />,
  left: <TextAlignLeft size={32} />,
  center: <TextAlignCenter size={32} />,
  right: <TextAlignRight size={32} />,
  justify: <TextAlignJustify size={32} />,
};
